// src/pages/widgets/WidgetVersionEditorPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Paper, CircularProgress, Alert,
  Stack, Switch, FormControlLabel, Select, MenuItem, InputLabel, FormControl,
  Breadcrumbs, Link as MuiLink, Divider, Grid, IconButton, Tooltip,
  Tabs, Tab,
} from '@mui/material';
import {
  Save as SaveIcon, ArrowBack as BackIcon, DeleteOutline as DeleteIcon,
  AddCircleOutline as AddRuleIcon, Code as EmbedIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { widgetService } from '../../api/widgetService';
import { agentService } from '../../api/agentService';
import { useModels } from '../../hooks/useModels';
import IdDisplayWithCopy from '../../components/common/IdDisplayWithCopy';
import EmbedCodeModal from '../../components/widgets/EmbedCodeModal';
import CodeEditorTab from '../../components/common/CodeEditorTab';

// TabPanel utility - This is critical for height management
function TabPanel(props) {
  const { children, value, index, sx, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`version-editor-tabpanel-${index}`}
      aria-labelledby={`version-editor-tab-${index}`}
      {...other}
      style={{
        // Only apply flex grow and height if it's the active tab
        // The parent of TabPanel (the Box after Tabs component) should be the main flex container for tab content
        display: value === index ? 'flex' : 'none', // Show/hide based on active state
        flexDirection: 'column',
        flexGrow: 1, // Allow TabPanel itself to grow
        height: '100%', // Try to make TabPanel take full height of its flex parent
        ...(sx || {}), // Allow overriding sx from props
      }}
    >
      {value === index && (
        // This Box is the direct parent of the tab's content (e.g., CodeEditorTab or Grid)
        <Box sx={{ 
            pt: 2.5, // Padding for content below tabs
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden', // Manage scroll within children if needed
            height: '100%', // If TabPanel has height, this can also take full height
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `version-editor-tab-${index}`,
    'aria-controls': `version-editor-tabpanel-${index}`,
  };
}

const WidgetVersionEditorPage = () => {
  const navigate = useNavigate();
  const { templateId: templateIdFromUrl, versionId } = useParams(); // Renamed to avoid conflict
  const location = useLocation();
  const isEditing = !!versionId;

  const initialJsContent = "/* Your Custom Widget JavaScript Here */\n\n// Access config: window.AGENT_PLATFORM_WIDGET_CONFIG\n// Expose API: \n// if (typeof window.banty === 'undefined') { window.banty = {}; }\n// window.banty.updateConfig = (newConfig) => { /* Logic for live config update */ };\n// window.banty.sendMessage = (text) => { /* Logic to send message via widget */ };";
  const initialSchemaContent = JSON.stringify({}, null, 2);

  const initialFormData = {
    version_name: '', description: '', agent_id: '',
    js_content: initialJsContent,
    config: {
      widgetTitle: "AI Assistant", placeholderWelcome: "Initializing chat...",
      welcomeStrategy: 'default', defaultWelcome: "Hello! How can I assist you today?",
      ruleBasedWelcome: [], llmWelcomePrompt: "As {botName} for {websiteName}, greet a user visiting {page_url}.",
      llmWelcomeModel: "", collapsedMessage: "Chat with {botName}",
      themeColor: "#007AFF", position: 'bottom-right',
    },
    config_schema: initialSchemaContent, ui_schema: initialSchemaContent,
    is_active: false, template_id: '', // Will be populated
  };

  const [formData, setFormData] = useState(initialFormData);
  const [initialLoadedData, setInitialLoadedData] = useState(null);
  const [parentTemplateInfo, setParentTemplateInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  
  const [availableAgents, setAvailableAgents] = useState([]);
  const [allModels, loadingModels] = useModels();

  const [embedModalProps, setEmbedModalProps] = useState({ open: false, versionId: null, templateId: null, templateName: null });
  const [currentTab, setCurrentTab] = useState(0);

  const prefillAgentId = location.state?.prefillAgentId;

  const fetchRequiredData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setFormErrors({});
    let fetchedAgents = [];

    try {
        const agentRes = await agentService.listAgents();
        fetchedAgents = (!agentRes.isError && Array.isArray(agentRes)) ? agentRes : [];
        setAvailableAgents(fetchedAgents);
        if (agentRes.isError) toast.warn("Could not load agents: " + (agentRes.message || "List empty"));

        const templateIdForFetch = isEditing ? (formData.template_id || templateIdFromUrl) : templateIdFromUrl;

        if (templateIdForFetch) {
            const templateRes = await widgetService.getTemplate(templateIdForFetch);
            if (templateRes.isError) {
                setError(prev => `${prev || ''} Failed to load parent template data.`.trim());
                setParentTemplateInfo(null);
            } else {
                setParentTemplateInfo(templateRes);
            }
        } else if (!isEditing && !templateIdFromUrl) {
            setError("Parent Template ID is missing. Cannot create or edit version.");
        }

        if (isEditing && versionId) {
            const versionRes = await widgetService.getVersion(versionId);
            if (versionRes.isError) {
                setError(prev => `${prev || ''} Failed to load version data.`.trim());
                // Keep existing formData if version load fails but template loaded
            } else {
                let currentAgentId = versionRes.agent_id || '';
                if (currentAgentId && fetchedAgents.length > 0 && !fetchedAgents.find(agent => agent.id === currentAgentId)) {
                    toast.warn(`Previously associated agent is no longer available. Please select an agent.`);
                    currentAgentId = '';
                }
                const loadedVersionData = {
                    ...initialFormData, ...versionRes,
                    agent_id: currentAgentId,
                    config: { ...initialFormData.config, ...(versionRes.config || {}), ruleBasedWelcome: Array.isArray(versionRes.config?.ruleBasedWelcome) ? versionRes.config.ruleBasedWelcome : [] },
                    js_content: versionRes.js_content || initialJsContent,
                    config_schema: typeof versionRes.config_schema === 'object' ? JSON.stringify(versionRes.config_schema, null, 2) : (versionRes.config_schema || initialSchemaContent),
                    ui_schema: typeof versionRes.ui_schema === 'object' ? JSON.stringify(versionRes.ui_schema, null, 2) : (versionRes.ui_schema || initialSchemaContent),
                    template_id: versionRes.template_id || templateIdFromUrl,
                };
                setFormData(loadedVersionData);
                setInitialLoadedData(JSON.parse(JSON.stringify(loadedVersionData)));

                if (!parentTemplateInfo && versionRes.template_id) { // If parentTemplateInfo wasn't set via URL param
                    const tplRes = await widgetService.getTemplate(versionRes.template_id);
                    if (!tplRes.isError) setParentTemplateInfo(tplRes);
                }
            }
        } else if (!isEditing) { // For new version
            const newVersionFormData = {
                ...initialFormData,
                agent_id: prefillAgentId && fetchedAgents.find(a => a.id === prefillAgentId) ? prefillAgentId : '',
                template_id: templateIdFromUrl || '', // Ensure template_id is set for new
            };
            setFormData(newVersionFormData);
            setInitialLoadedData(JSON.parse(JSON.stringify(newVersionFormData)));
        }
    } catch (e) {
        console.error("Error in fetchRequiredData:", e);
        setError("An unexpected error occurred while loading data.");
    } finally {
        setLoading(false);
    }
  }, [isEditing, versionId, templateIdFromUrl, prefillAgentId]); // Removed formData.template_id from deps to avoid loop with setFormData

  useEffect(() => {
    fetchRequiredData();
  }, [fetchRequiredData]);
  
  const handleFormChange = (e) => { /* ... same as before ... */ 
    const { name, value, type, checked } = e.target;
    setError(null);
    if (name.startsWith("config.")) {
      const configField = name.split(".")[1];
      setFormData(prev => ({ ...prev, config: { ...prev.config, [configField]: type === 'checkbox' ? checked : value }}));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: null }));
  };
  const handleJsCodeChange = (newCode) => setFormData(prev => ({ ...prev, js_content: newCode === undefined ? '' : newCode }));
  const handleConfigSchemaChange = (newCode) => setFormData(prev => ({ ...prev, config_schema: newCode === undefined ? '' : newCode }));
  const handleUiSchemaChange = (newCode) => setFormData(prev => ({ ...prev, ui_schema: newCode === undefined ? '' : newCode }));

  const handleResetJsCode = () => setFormData(prev => ({ ...prev, js_content: initialLoadedData?.js_content || initialJsContent }));
  const handleResetConfigSchema = () => setFormData(prev => ({ ...prev, config_schema: initialLoadedData?.config_schema || initialSchemaContent }));
  const handleResetUiSchema = () => setFormData(prev => ({ ...prev, ui_schema: initialLoadedData?.ui_schema || initialSchemaContent }));
  
  const handleRuleChange = (index, field, value) => { /* ... same ... */ 
    const newRules = [...(formData.config.ruleBasedWelcome || [])];
    if (newRules[index]) {
    newRules[index] = { ...newRules[index], [field]: value };
    setFormData(prev => ({ ...prev, config: { ...prev.config, ruleBasedWelcome: newRules }}));
    }
  };
  const handleAddRule = () => { /* ... same ... */ 
    const newRules = [...(formData.config.ruleBasedWelcome || []), { if_url_contains: '', if_hostname_is: '', message: '' }];
    setFormData(prev => ({ ...prev, config: { ...prev.config, ruleBasedWelcome: newRules }}));
  };
  const handleRemoveRule = (index) => { /* ... same ... */ 
    const newRules = [...(formData.config.ruleBasedWelcome || [])];
    newRules.splice(index, 1);
    setFormData(prev => ({ ...prev, config: { ...prev.config, ruleBasedWelcome: newRules }}));
  };
  const validateForm = () => { /* ... same, ensure JSON parse checks handle empty strings gracefully ... */ 
    const errors = {};
    if (!formData.version_name.trim()) errors.version_name = 'Version name is required.';
    if (!formData.agent_id) errors.agent_id = 'Agent selection is required.';
    if (!formData.js_content || formData.js_content.trim().length < 20) {
         errors.js_content = 'JavaScript Content seems too short or is missing essential parts.';
    }
    
    const validateJsonString = (jsonString, fieldName) => {
        if (jsonString && jsonString.trim() !== '' && jsonString.trim() !== '{}') {
            try { JSON.parse(jsonString); } 
            catch (e) { return `${fieldName} is not valid JSON: ${e.message}`; }
        }
        return null;
    };
    errors.config_schema = validateJsonString(formData.config_schema, "Config Schema");
    errors.ui_schema = validateJsonString(formData.ui_schema, "UI Schema");
    
    const validErrors = Object.fromEntries(Object.entries(errors).filter(([_, v]) => v !== null));
    setFormErrors(validErrors);
    return Object.keys(validErrors).length === 0;
  };
  const handleSubmit = async (e) => { /* ... same, ensure schema parsing is robust ... */ 
    e.preventDefault();
    setError(null);
    setFormErrors({});
    if (!validateForm()) {
    toast.error('Please correct the form errors.');
    return;
    }

    setSaving(true);
    let parsedConfigSchema = null;
    let parsedUiSchema = null;
    try { if(formData.config_schema && formData.config_schema.trim() !== '' && formData.config_schema.trim() !== '{}') parsedConfigSchema = JSON.parse(formData.config_schema); }
    catch (e) { /* error already caught by validateForm */ }
    try { if(formData.ui_schema && formData.ui_schema.trim() !== '' && formData.ui_schema.trim() !== '{}') parsedUiSchema = JSON.parse(formData.ui_schema); }
    catch (e) { /* error already caught by validateForm */ }

    const currentTemplateIdForPayload = parentTemplateInfo?.id || formData.template_id || templateIdFromUrl;
    if (!currentTemplateIdForPayload) {
        toast.error("Parent Template ID is missing. Cannot save.");
        setError("Parent Template ID is missing.");
        setSaving(false);
        return;
    }

    const payload = {
        // Explicitly map fields for clarity and to avoid sending extra state like 'template_id'
        version_name: formData.version_name,
        description: formData.description,
        agent_id: formData.agent_id,
        js_content: formData.js_content,
        config: formData.config,
        config_schema: parsedConfigSchema,
        ui_schema: parsedUiSchema,
        is_active: formData.is_active,
        widget_template_id: currentTemplateIdForPayload, 
        name: formData.version_name, // For service compatibility if it expects 'name'
    };
    
    let res;
    if (isEditing) {
        res = await widgetService.updateVersion(versionId, payload);
    } else {
        res = await widgetService.createVersion(payload);
    }
    setSaving(false);

    if (res.isError) {
        setError(res.message || 'Save failed.');
        toast.error(res.message || 'Save failed.');
    } else {
        toast.success(`Widget version ${isEditing ? 'updated' : 'created'} successfully.`);
        // Navigation logic (same as before)
        const cameFromAgentContext = location.state?.fromAgentContext;
        const prefillAgentIdFromState = location.state?.prefillAgentId;

        if (cameFromAgentContext && prefillAgentIdFromState) {
            navigate(`/agent-workspace/${prefillAgentIdFromState}/widgets`);
        } else if (currentTemplateIdForPayload) { // Use the determined template ID for navigation
            navigate(`/widgets/${currentTemplateIdForPayload}/versions`);
        } else { 
            navigate('/widgets');
        }
    }
  };
  const handleOpenEmbedModalForCurrentVersion = () => { /* ... same ... */ 
    if (isEditing && versionId && parentTemplateInfo) {
    setEmbedModalProps({
        open: true,
        versionId: versionId,
        templateId: parentTemplateInfo.id,
        templateName: parentTemplateInfo.name,
    });
    } else if (!isEditing) {
    toast.info("Save the version first to get its embed code.");
    }
  };
  const handleCloseEmbedModal = () => { /* ... same ... */ 
    setEmbedModalProps({ open: false, versionId: null, templateId: null, templateName: null });
  };
  const handleBackNavigation = () => { /* ... same, ensure template ID for nav is robust ... */ 
    const currentTemplateIdForNav = parentTemplateInfo?.id || formData.template_id || templateIdFromUrl;
    const cameFromAgentContext = location.state?.fromAgentContext;
    const prefillAgentIdFromState = location.state?.prefillAgentId;
    if (cameFromAgentContext && prefillAgentIdFromState) {
    navigate(`/agent-workspace/${prefillAgentIdFromState}/widgets`);
    } else if (currentTemplateIdForNav) {
    navigate(`/widgets/${currentTemplateIdForNav}/versions`);
    }
     else {
    navigate('/widgets'); 
    }
  };
  const handleTabChange = (event, newValue) => setCurrentTab(newValue);

  if (loading) return <Box sx={{display:'flex', justifyContent:'center', alignItems:'center', height: '70vh'}}><CircularProgress size={50}/><Typography sx={{ml:2}}>Loading editor...</Typography></Box>;
  if (error && !formData.version_name && !parentTemplateInfo && !isEditing) {
    return <Alert severity="error" sx={{m:3}} action={<Button onClick={handleBackNavigation}>Go Back</Button>}>{error}</Alert>
  }

  return (
    <Paper sx={{ 
        p: {xs: 2, sm: 2.5, md: 3}, // Consistent padding
        maxWidth: 1200, // Allow slightly wider for better tab layout
        mx: 'auto', 
        display: 'flex',
        flexDirection: 'column',
        minHeight: {md: 'calc(100vh - 120px)'}, // Approximate viewport height minus headers
        overflow: 'hidden', // Let internal components manage their scroll
    }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        {/* ... Breadcrumbs logic ... */}
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/widgets">
          Widget Templates
        </MuiLink>
        {parentTemplateInfo && (
          <MuiLink component={RouterLink} underline="hover" color="inherit" to={`/widgets/${parentTemplateInfo.id}/versions`}>
            {parentTemplateInfo.name || `Template ...${(parentTemplateInfo.id || '').slice(-4)}`}
          </MuiLink>
        )}
        <Typography color="text.primary">
          {isEditing ? `Edit: ${formData.version_name || 'Version'}` : 'Create New Version'}
        </Typography>
      </Breadcrumbs>

      <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems={{xs:'flex-start', sm:'center'}} mb={{xs:2, sm:3}} gap={1}>
        {/* ... Title, ID Display, Embed Button ... */}
        <Typography variant="h4" component="h1">
          {isEditing ? `Edit Widget Version` : `Create New Version ${parentTemplateInfo ? `for "${parentTemplateInfo.name}"`: ''}`}
        </Typography>
        {isEditing && versionId && (
             <Stack direction="row" spacing={1} alignItems="center">
                <IdDisplayWithCopy id={versionId} label="Version ID" />
                <Button 
                    variant="outlined" 
                    size="small"
                    startIcon={<EmbedIcon />} 
                    onClick={handleOpenEmbedModalForCurrentVersion}
                    disabled={saving || loading || !parentTemplateInfo}
                  >
                    Get Embed Code
                  </Button>
             </Stack>
        )}
      </Stack>

      {error && formData.version_name && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>} {/* Show non-critical errors here */}

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow:'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="widget version editor tabs" variant="scrollable" scrollButtons="auto">
            <Tab label="General & Config" {...a11yProps(0)} />
            <Tab label="JavaScript Content" {...a11yProps(1)} />
            <Tab label="Advanced Schemas" {...a11yProps(2)} />
          </Tabs>
        </Box>

        {/* This Box will contain the TabPanels and needs to allow them to grow */}
        <Box sx={{flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' /* For TabPanel absolute positioning if needed */ }}>
            <TabPanel value={currentTab} index={0}>
            {/* General & Config Content (Grid layout) */}
            {/* ... (Your existing Grid layout for General & Config, ensure TextFields use handleFormChange) ... */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={5}> {/* Core Details Column */}
                <Typography variant="h6" gutterBottom sx={{fontSize: '1.2rem', fontWeight:500}}>Core Details</Typography>
                <TextField fullWidth label="Version Name / Tag" name="version_name" value={formData.version_name} onChange={handleFormChange} required margin="normal" disabled={saving} error={!!formErrors.version_name} helperText={formErrors.version_name || "E.g., v1.0.0, 'Beta Launch'"} />
                <TextField fullWidth label="Description (Optional)" name="description" value={formData.description || ''} onChange={handleFormChange} margin="normal" multiline rows={3} disabled={saving} />
                
                <FormControl fullWidth margin="normal" required error={!!formErrors.agent_id}>
                    <InputLabel id="agent-select-label">Associated Agent</InputLabel>
                    <Select labelId="agent-select-label" name="agent_id" value={formData.agent_id} label="Associated Agent" onChange={handleFormChange} disabled={saving || availableAgents.length === 0}>
                    {availableAgents.length === 0 && !loading && <MenuItem value="" disabled><em>No agents available or error loading</em></MenuItem>}
                    {loading && availableAgents.length === 0 && <MenuItem value="" disabled><em>Loading agents...</em></MenuItem>}
                    {availableAgents.map(agent => ( <MenuItem key={agent.id} value={agent.id}>{agent.name}</MenuItem> ))}
                    </Select>
                    {formErrors.agent_id && <Typography color="error" variant="caption">{formErrors.agent_id}</Typography>}
                </FormControl>
                <FormControlLabel control={<Switch checked={formData.is_active} onChange={handleFormChange} name="is_active" disabled={saving}/>} label="Set as Active Version for this Template" sx={{mt:1}}/>
                </Grid>

                <Grid item xs={12} md={7}> {/* Widget Configuration Column */}
                <Typography variant="h6" gutterBottom sx={{fontSize: '1.2rem', fontWeight:500}}>Widget Configuration (<code>config</code> JSON)</Typography>
                <TextField fullWidth label="Widget Title" name="config.widgetTitle" value={formData.config.widgetTitle || ''} onChange={handleFormChange} disabled={saving} margin="dense"/>
                <TextField fullWidth label="Collapsed Message" name="config.collapsedMessage" value={formData.config.collapsedMessage || ''} onChange={handleFormChange} disabled={saving} helperText="Use {botName}" margin="dense"/>
                <TextField fullWidth label="Placeholder Welcome" name="config.placeholderWelcome" value={formData.config.placeholderWelcome || ''} onChange={handleFormChange} disabled={saving} margin="dense"/>
                <Box sx={{mt:1, mb:1}}>
                    <Typography variant="caption" component="label" htmlFor="themeColorPicker" sx={{display:'block', color: theme => theme.palette.text.secondary, mb:0.5}}>Theme Color (Basic)</Typography>
                    <input id="themeColorPicker" type="color" name="config.themeColor" value={formData.config.themeColor || '#007AFF'} onChange={handleFormChange} disabled={saving} style={{width: '100%', height: '40px', border: theme=>`1px solid ${theme.palette.divider}`, borderRadius: theme=>theme.shape.borderRadius / 2, padding: '4px', boxSizing:'border-box'}}/>
                </Box>
                        
                    <Divider sx={{my:2}}>Welcome Strategy</Divider>
                    <FormControl fullWidth disabled={saving} margin="dense">
                        <InputLabel>Welcome Strategy</InputLabel>
                        <Select name="config.welcomeStrategy" value={formData.config.welcomeStrategy || 'default'} label="Welcome Strategy" onChange={handleFormChange}>
                            <MenuItem value="default">Default Message</MenuItem>
                            <MenuItem value="rules">Rule-Based</MenuItem>
                            <MenuItem value="llm">LLM-Powered</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField fullWidth label="Default Welcome Message" name="config.defaultWelcome" value={formData.config.defaultWelcome || ''} onChange={handleFormChange} multiline rows={2} disabled={saving} helperText="Used for 'Default' or as fallback." margin="dense" sx={{mt:2}}/>

                    {formData.config.welcomeStrategy === 'rules' && (
                        <Box sx={{mt:2}}> {/* ... Rule UI ... */} </Box>
                    )}
                    {formData.config.welcomeStrategy === 'llm' && (
                        <Box sx={{mt:2}}> {/* ... LLM UI ... */} </Box>
                    )}
                </Grid>
            </Grid>
            </TabPanel>

            <TabPanel value={currentTab} index={1} sx={{overflow: 'auto' /* Allow scroll if content overflows */}}>
            <CodeEditorTab
                isActiveTab={currentTab === 1}
                code={formData.js_content}
                initialCode={initialLoadedData?.js_content || initialJsContent}
                onCodeChange={handleJsCodeChange}
                onResetCode={handleResetJsCode}
                language="javascript"
                title="Widget JavaScript Content"
                infoAlertText="This JS is served to clients. It initializes the widget, uses window.AGENT_PLATFORM_WIDGET_CONFIG, and should expose window.banty.updateConfig()."
                minEditorHeight="calc(100vh - 400px)" // Example: try to make it responsive to viewport
            />
            </TabPanel>

            <TabPanel value={currentTab} index={2} sx={{overflow: 'auto'}}>
            <Grid container spacing={2} sx={{flexGrow: 1, display: 'flex' /* Removed specific flexDirection for Grid */}}>
                <Grid item xs={12} md={6} sx={{display: 'flex', flexDirection: 'column'}}>
                <CodeEditorTab
                    isActiveTab={currentTab === 2}
                    code={formData.config_schema}
                    initialCode={initialLoadedData?.config_schema || initialSchemaContent}
                    onCodeChange={handleConfigSchemaChange}
                    onResetCode={handleResetConfigSchema}
                    language="json"
                    title="Config Schema (JSON)"
                    infoAlertText="Defines structure for TestPage live config (optional)."
                    minEditorHeight="calc(100vh - 420px)"
                />
                {formErrors.config_schema && <Typography color="error" variant="caption" sx={{display: 'block', mt:0.5, ml:0.5}}>{formErrors.config_schema}</Typography>}
                </Grid>
                <Grid item xs={12} md={6} sx={{display: 'flex', flexDirection: 'column'}}>
                <CodeEditorTab
                    isActiveTab={currentTab === 2}
                    code={formData.ui_schema}
                    initialCode={initialLoadedData?.ui_schema || initialSchemaContent}
                    onCodeChange={handleUiSchemaChange}
                    onResetCode={handleResetUiSchema}
                    language="json"
                    title="UI Schema (JSON)"
                    infoAlertText="Defines layout for TestPage live config (optional)."
                    minEditorHeight="calc(100vh - 420px)"
                />
                {formErrors.ui_schema && <Typography color="error" variant="caption" sx={{display: 'block', mt:0.5, ml:0.5}}>{formErrors.ui_schema}</Typography>}
                </Grid>
            </Grid>
            </TabPanel>
        </Box> {/* End of Box containing TabPanels */}
        
        <Stack direction="row" justifyContent="space-between" mt="auto" pt={3} spacing={2} sx={{borderTop: 1, borderColor:'divider', position:'sticky', bottom:0, bgcolor:'background.paper', zIndex:1, pb:2, px:0 /* Padding handled by main paper */ }}>
          <Button variant="outlined" onClick={handleBackNavigation} startIcon={<BackIcon />} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} disabled={saving || loading}>
            {saving ? 'Saving...' : (isEditing ? 'Save Version Changes' : 'Create Version')}
          </Button>
        </Stack>
      </Box>
      <EmbedCodeModal
        open={embedModalProps.open}
        onClose={handleCloseEmbedModal}
        versionId={embedModalProps.versionId}
        templateId={embedModalProps.templateId}
        templateName={embedModalProps.templateName}
      />
    </Paper>
  );
};

export default WidgetVersionEditorPage;